/**
 * The pieces of the tabbed meeting series page (#290): the tab bar, the
 * meeting list split into upcoming and past, and the task / note lists of
 * work made in the series' meetings or linked to the series.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, CircleCheck, Circle, StickyNote, Link2, SquareCheckBig, UserCheck, Scissors, Settings, type LucideIcon } from 'lucide-react';
import { formatRelativeDate } from '../../lib/relativeTime';
import { PastMeetings, itemCounts, KIND_TEXT, type MeetingItem } from './PastMeetings';

export const SERIES_TABS = ['meetings', 'tasks', 'notes', 'follow-ups', 'excerpts', 'settings'] as const;
export type SeriesTab = typeof SERIES_TABS[number];

const TAB_LABEL: Record<SeriesTab, string> = {
  meetings: 'Meetings', tasks: 'Tasks', notes: 'Notes', 'follow-ups': 'Follow-ups', excerpts: 'Excerpts', settings: 'Settings',
};
/** Tasks, notes and excerpts wear the colours they have everywhere else, follow-ups the people blue; the rest use the accent. */
const TAB_TONE: Partial<Record<SeriesTab, { icon: string; underline: string }>> = {
  tasks: { icon: 'text-excerpt-task', underline: 'border-excerpt-task' },
  notes: { icon: 'text-excerpt-note', underline: 'border-excerpt-note' },
  'follow-ups': { icon: 'text-follow-up', underline: 'border-follow-up' },
  excerpts: { icon: 'text-excerpt', underline: 'border-excerpt' },
};
const TAB_ICON: Record<SeriesTab, LucideIcon> = {
  meetings: Calendar, tasks: SquareCheckBig, notes: StickyNote, 'follow-ups': UserCheck, excerpts: Scissors, settings: Settings,
};

/** The tab named in the URL, or Meetings for anything missing or unknown. */
export function parseSeriesTab(value: string | null): SeriesTab {
  return (SERIES_TABS as readonly string[]).includes(value ?? '') ? value as SeriesTab : 'meetings';
}

export interface SeriesItem {
  id: string;
  title: string;
  origin: 'meeting' | 'linked';
  linked: boolean;
  meeting_id: string | null;
  meeting_date: string | null;
  status?: string;
  priority?: string;
  due_date?: string | null;
  assignee_name?: string | null;
}

export interface SeriesItems { tasks: SeriesItem[]; notes: SeriesItem[] }

export function SeriesTabBar({ tab, counts, onChange }: {
  tab: SeriesTab;
  counts: Partial<Record<SeriesTab, number>>;
  onChange: (tab: SeriesTab) => void;
}) {
  // Same narrow-width behaviour as the project tabs (#269): the strip scrolls
  // without a visible scrollbar, fades at the edge, and under 640px inactive
  // tabs drop to icon + count so all six fit a phone.
  return (
    <div className="@container mb-5">
      <div
        role="tablist"
        aria-label="Series sections"
        data-testid="series-tabs"
        className="flex gap-1 overflow-x-auto border-b border-border [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,#000_calc(100%-24px),transparent)]"
      >
        {SERIES_TABS.map((t) => {
          const active = t === tab;
          const Icon = TAB_ICON[t];
          const tone = TAB_TONE[t];
          return (
            <button
              key={t}
              type="button"
              role="tab"
              id={`series-tab-${t}`}
              aria-selected={active}
              aria-controls="series-tabpanel"
              aria-label={counts[t] !== undefined ? `${TAB_LABEL[t]} (${counts[t]})` : TAB_LABEL[t]}
              title={TAB_LABEL[t]}
              onClick={() => onChange(t)}
              className={`${t === 'settings' ? 'ml-auto ' : ''}flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 @max-[640px]:px-2.5 py-2.5 text-sm transition-colors ${
                active ? `${tone?.underline ?? 'border-accent'} font-semibold text-text-primary` : 'border-transparent text-text-secondary hover:text-text-primary'}`}
            >
              <Icon size={14} aria-hidden="true" className={tone?.icon} />
              <span className={active ? '' : '@max-[640px]:hidden'}>{TAB_LABEL[t]}</span>
              {counts[t] !== undefined && <span className="text-xs font-normal text-text-muted">{counts[t]}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface SeriesMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  summary: string | null;
}

/**
 * Upcoming meetings (today on) soonest first, then past ones grouped by recency (#302), each with what came out of it.
 * Also lists one-off meetings on the Meetings page (#308), hence `meetingHref` and `actions`.
 */
export function SeriesMeetingList({ meetingHref, actions, expandAll, meetings, today, itemsByMeeting, withNotes, formatWhen }: {
  meetingHref: (id: string) => string;
  actions?: (m: SeriesMeeting) => ReactNode;
  expandAll?: boolean;
  meetings: SeriesMeeting[];
  today: string;
  itemsByMeeting: Map<string, MeetingItem[]>;
  withNotes: Set<string>;
  formatWhen: (m: SeriesMeeting) => { heading: string; sub: string | null; when: string | null };
}) {
  if (meetings.length === 0) {
    return (
      <div className="text-center py-16 text-text-muted">
        <Calendar size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-lg mb-2">No meetings yet</p>
        <p className="text-sm">Create a new meeting instance to start taking notes.</p>
      </div>
    );
  }
  const upcoming = meetings.filter((m) => m.meeting_date >= today).sort((a, b) => a.meeting_date.localeCompare(b.meeting_date));
  const past = meetings.filter((m) => m.meeting_date < today).sort((a, b) => b.meeting_date.localeCompare(a.meeting_date));

  const row = (m: SeriesMeeting) => {
    const { heading, sub } = formatWhen(m);
    const counts = itemCounts(itemsByMeeting.get(m.id) ?? []);
    return (
      <div key={m.id} data-testid="series-meeting"
        className="flex items-start gap-2 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group">
        <Link to={meetingHref(m.id)} className="flex min-w-0 flex-1 items-start justify-between gap-3 p-4">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-text-primary group-hover:text-accent transition-colors [overflow-wrap:anywhere]">{heading}</h3>
            {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
            {m.summary && <p className="text-xs text-text-muted mt-1 truncate max-w-lg">{m.summary}</p>}
            {counts.length > 0 && (
              <div className="mt-1.5 flex gap-3 text-xs">
                {counts.map((c) => <span key={c.kind} className={KIND_TEXT[c.kind]}>{c.text}</span>)}
              </div>
            )}
          </div>
          <span className="shrink-0 text-xs text-text-muted">{formatRelativeDate(m.meeting_date)}</span>
        </Link>
        {actions && <div className="shrink-0 py-3 pr-3">{actions(m)}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {upcoming.length > 0 && (
        <section aria-label="Upcoming meetings" className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">Upcoming</h2>
          {upcoming.map(row)}
        </section>
      )}
      {past.length > 0 && (
        <PastMeetings meetingHref={meetingHref} actions={actions} expandAll={expandAll} meetings={past} today={today} itemsByMeeting={itemsByMeeting} withNotes={withNotes} formatWhen={formatWhen} />
      )}
    </div>
  );
}

function shortDate(d: string): string {
  return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Where an item came from: the meeting (a link to it), "linked", or both. */
function Origin({ item, seriesId }: { item: SeriesItem; seriesId: string }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {item.origin === 'meeting' && item.meeting_id && item.meeting_date && (
        <Link to={`/meetings/${seriesId}/${item.meeting_id}`} className="underline decoration-dotted hover:text-text-primary">
          from {shortDate(item.meeting_date)}
        </Link>
      )}
      {item.linked && (
        <span className="inline-flex items-center gap-0.5" title="Linked to this series"><Link2 size={11} /> linked</span>
      )}
    </span>
  );
}

export function SeriesItemList({ kind, items, seriesId }: { kind: 'task' | 'note'; items: SeriesItem[]; seriesId: string }) {
  if (items.length === 0) {
    return (
      <p className="py-6 text-sm text-text-muted">
        {kind === 'task'
          ? 'No tasks yet. Tasks made in these meetings, or linked to the series, show up here.'
          : 'No notes yet. Notes made in these meetings, or linked to the series, show up here.'}
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border rounded-lg border border-border" data-testid={`series-${kind}s`}>
      {items.map((item) => {
        const done = item.status === 'done' || item.status === 'cancelled';
        const Icon = kind === 'note' ? StickyNote : done ? CircleCheck : Circle;
        return (
          <li key={item.id} className="flex items-start gap-3 px-4 py-2.5">
            <Icon size={15} className={`mt-0.5 shrink-0 ${kind === 'note' ? 'text-excerpt-note' : 'text-excerpt-task'}`} />
            <div className="min-w-0 flex-1">
              <Link
                to={kind === 'task' ? `/tasks/${item.id}` : `/notes/${item.id}`}
                className={`text-sm hover:text-accent [overflow-wrap:anywhere] ${done ? 'text-text-muted line-through' : 'text-text-primary'}`}
              >
                {item.title || '(untitled)'}
              </Link>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-text-muted">
                <Origin item={item} seriesId={seriesId} />
                {item.assignee_name && <span>{item.assignee_name}</span>}
                {item.due_date && !done && <span>due {shortDate(item.due_date)}</span>}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
